import { CRATE_SIZES, CRATE_TYPES, SUP_CATS } from './constants.ts';
import { MILLI, fmtCount, fmtSCU } from './quantity.ts';
import { haveOf, measureOf, placeKey, prepState, type PrepState } from './rules.ts';
import type { Item, Lot, LotPlace, PrepLevel } from './schemas.ts';

// The Supply page's groupings and the low-stock list, after Sierra's crateGroups
// and matGroups (lines 687 and 694) and lowStock (line 678). The page, the reports
// and (Chunk 6) the badges and widgets all use them, so they agree.

/** A prep level with what its place has now. */
export interface LevelState {
  level: PrepLevel;
  item: Item;
  have: number;
  state: PrepState;
}

/** Every active prep level with its state (R1, R2). */
export function levelStates(items: readonly Item[], lots: readonly Lot[], levels: readonly PrepLevel[]): LevelState[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  return levels.flatMap((level) => {
    const item = byId.get(level.item);
    if (item === undefined || !level.active) return [];
    const have = haveOf(item, level.place, lots);
    return [{ level, item, have, state: prepState(level.place.kind, have, level.target) }];
  });
}

/** Stockpile prep levels that are low: the Supply badge, "To acquire" and the acquisition list (R18). */
export function lowStock(items: readonly Item[], lots: readonly Lot[], levels: readonly PrepLevel[]): LevelState[] {
  return levelStates(items, lots, levels).filter((entry) => entry.level.place.kind === 'stockpile' && entry.state === 'low');
}

/** "64 SCU", "3 kits", "12": an amount in an item's measure. */
export function measureText(item: Pick<Item, 'unit'>, amount: number): string {
  const measure = measureOf(item.unit);
  if (measure === 'scu') return `${fmtSCU(amount)} SCU`;
  if (measure === 'kits') return `${fmtCount(amount)} kit${amount === 1 ? '' : 's'}`;
  return fmtCount(amount);
}

/** A lot's amount in its own unit: "3", "2.569 SCU". */
export function amountText(item: Pick<Item, 'unit'>, amount: number): string {
  return item.unit === 'scu' ? `${fmtSCU(amount)} SCU` : fmtCount(amount);
}

/** Where a lot is, as people read it: its location, or its ship's name. */
export function placeText(place: LotPlace, shipNames: ReadonlyMap<string, string>): string {
  return place.kind === 'stockpile' ? place.location : (shipNames.get(place.shipId) ?? 'a ship');
}

/** A material lot's state and quality: "refined Q500", "unrefined". */
export function materialText(lot: Pick<Lot, 'refined' | 'quality'>): string {
  return `${lot.refined ? 'refined' : 'unrefined'}${lot.quality === null ? '' : ` Q${String(lot.quality)}`}`;
}

/** One lot as the panel names it (`supName`, line 684). */
export function lotName(item: Item, lot: Lot): string {
  if (item.category === 'Crates') return `${String(lot.crateSize ?? '?')} SCU crate (${item.name})`;
  if (item.category === 'Kits') return `${item.name}${lot.boxSize === null ? '' : ` · ${String(lot.boxSize)} SCU box`}`;
  if (item.category === 'Materials') return `${item.name} (${lot.refined ? 'refined' : 'unrefined'}${lot.quality === null ? '' : `, Q${String(lot.quality)}`})`;
  return item.name;
}

/** Locations in first-seen order. */
function locationsOf(lots: readonly Lot[]): string[] {
  const seen = new Map<string, string>();
  for (const lot of lots) if (lot.place.kind === 'stockpile' && !seen.has(lot.place.locationKey)) seen.set(lot.place.locationKey, lot.place.location);
  return [...seen.values()];
}

/** One crate item's lots: counts by size, crates and SCU, and where they are. */
export interface CrateGroup {
  item: Item;
  lots: Lot[];
  /** Crates by size. */
  bySize: Map<number, number>;
  count: number;
  /** Thousandths of an SCU. */
  scu: number;
  locations: string[];
}

/** Crates grouped by item (`crateGroups`, line 687): by crate type, then name; lots by size, then location. */
export function crateGroups(items: readonly Item[], lots: readonly Lot[]): CrateGroup[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const groups = new Map<string, CrateGroup>();
  for (const lot of lots) {
    const item = byId.get(lot.item);
    if (item?.category !== 'Crates') continue;
    const group = groups.get(item.id) ?? { item, lots: [], bySize: new Map<number, number>(), count: 0, scu: 0, locations: [] };
    groups.set(item.id, group);
    const size = lot.crateSize ?? 0;
    group.lots.push(lot);
    group.bySize.set(size, (group.bySize.get(size) ?? 0) + lot.amount);
    group.count += lot.amount;
    group.scu += size * MILLI * lot.amount;
  }
  const typeRank = (item: Item) => (item.crateType === null ? CRATE_TYPES.length : CRATE_TYPES.indexOf(item.crateType));
  return [...groups.values()]
    .map((group) => {
      group.lots.sort((a, b) => (a.crateSize ?? 0) - (b.crateSize ?? 0) || placeKey(a.place).localeCompare(placeKey(b.place)));
      group.locations = locationsOf(group.lots);
      return group;
    })
    .sort((a, b) => typeRank(a.item) - typeRank(b.item) || a.item.name.localeCompare(b.item.name));
}

/** "8 SCU ×4, 32 SCU ×1": a crate group's sizes, smallest first. */
export function sizesText(group: CrateGroup): string {
  return CRATE_SIZES.filter((size) => (group.bySize.get(size) ?? 0) > 0)
    .map((size) => `${String(size)} SCU ×${fmtCount(group.bySize.get(size) ?? 0)}`)
    .join(', ');
}

/** One material's lots: refined and unrefined SCU, the quality range, and where they are. */
export interface MaterialGroup {
  item: Item;
  lots: Lot[];
  refined: number;
  unrefined: number;
  total: number;
  qualityMin: number | null;
  qualityMax: number | null;
  locations: string[];
}

/** Materials grouped by item (`matGroups`, line 694), by name; lots refined first, then by quality, highest first. */
export function materialGroups(items: readonly Item[], lots: readonly Lot[]): MaterialGroup[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const groups = new Map<string, MaterialGroup>();
  for (const lot of lots) {
    const item = byId.get(lot.item);
    if (item?.category !== 'Materials') continue;
    const group = groups.get(item.id) ?? { item, lots: [], refined: 0, unrefined: 0, total: 0, qualityMin: null, qualityMax: null, locations: [] };
    groups.set(item.id, group);
    group.lots.push(lot);
    group.total += lot.amount;
    if (lot.refined) group.refined += lot.amount;
    else group.unrefined += lot.amount;
    if (lot.quality !== null) {
      group.qualityMin = Math.min(group.qualityMin ?? lot.quality, lot.quality);
      group.qualityMax = Math.max(group.qualityMax ?? lot.quality, lot.quality);
    }
  }
  return [...groups.values()]
    .map((group) => {
      group.lots.sort((a, b) => Number(b.refined) - Number(a.refined) || (b.quality ?? -1) - (a.quality ?? -1));
      group.locations = locationsOf(group.lots);
      return group;
    })
    .sort((a, b) => a.item.name.localeCompare(b.item.name));
}

/** The categories that have lots, in Sierra's order. */
export function categoriesOf(items: readonly Item[], lots: readonly Lot[]): Item['category'][] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const present = new Set(lots.map((lot) => byId.get(lot.item)?.category));
  return SUP_CATS.filter((category) => present.has(category));
}
